import React from 'react';
import classNames from 'classnames';
import dompurify from 'dompurify';
import arrowDown from '../../../assets/arrowDown.svg';
import arrowDownWhite from '../../../assets/arrowDownWhite.svg';
import { getHeaderClasses, getHeaderClassesMap } from '../../../utils/getHeaderClasses';
import { sanitizeOrPassthrough } from '../../../helpers/rteHelpers';
import { SsrTagClassMap } from '../../../helpers/rteSsrSanitizer';

interface AccordionItemProps {
  index: number;
  item: AccordionItemData;
  isOpen: boolean;
  textClass: string;
  hasText: boolean;
  onToggle: (index: number) => void;
  openIndexes: number[];
  
}

export interface AccordionItemData {
  title: { value: string };
  sectionDescription: { value: string };
  content: { value: string };
}

const localPurifier = dompurify();

export const AccordionItem: React.FC<AccordionItemProps> = ({
  index,
  item,
  isOpen,
  textClass,
  hasText,
  onToggle,
  openIndexes,
}) => {
  const getArrowProps = (isMobile: boolean) => {
    const baseProps = {
      className: `h-[18px] ${isMobile ? 'md:hidden' : 'hidden md:block'}`,
      src: textClass === 'text-white' ? arrowDownWhite : arrowDown,
    };
  
    if (!isOpen) {
      return {
        ...baseProps,
        style: { transform: isMobile && hasText ? 'rotate(270deg)' : 'none' },
      };
    }
  
    return {
      ...baseProps,
      style: { 
        transform: isMobile && hasText ? 'none' : 'rotate(180deg)',
      },
    };
  };

  // Mirrors the "beforeSanitizeAttributes" hook below, for SSR markup to match the client.
  const ssrTagClasses: SsrTagClassMap = getHeaderClassesMap((level) => getHeaderClasses(level, textClass));

  localPurifier.addHook("beforeSanitizeAttributes", (node) => {
    if (node.nodeName?.match(/^H[2-6]$/)) {
      node.setAttribute("class", ssrTagClasses[node.nodeName.toLowerCase()]);
    }
  });

  return (
    <div 
      className={classNames(
        "mb-0 bg-transparent border-b border-primary-200 accordion-content",
        hasText && openIndexes.length > 0 && !openIndexes.includes(index) ? 'hidden md:block' : 'block'
      )}
    >
      <button
        className="flex justify-between w-full px-4 py-3 pl-0 text-left border-none outline-none focus:border-none focus:outline-none hover:border-none hover:outline-none"
        onClick={() => onToggle(index)}
        aria-expanded={isOpen}
        aria-controls={`accordion-content-${index}`}
      >
        <div className="flex flex-col">
          <h3 className={classNames("text-lg", textClass, hasText ? 'text-2xl font-normal' : 'font-semibold')}>
            {item.title.value}
          </h3>
          <h4 className={classNames("text-lg", textClass, hasText ? 'text-xl font-normal' : 'font-semibold')}>
            {item.sectionDescription.value}
          </h4>
        </div>
        <span className={classNames("flex flex-shrink-0 items-center mt-1", textClass === 'text-white' && 'text-white')}>
          {isOpen && hasText && <span className="block md:hidden mr-[12px]">Back</span>}
          <img {...getArrowProps(false)} />
          <img {...getArrowProps(true)} /> 
        </span>
      </button>
      <div 
        id={`accordion-content-${index}`}
        role="region"
        aria-labelledby={`accordion-header-${index}`}
        className={classNames(
          "px-4 pl-0 transition-all duration-200 overflow-hidden",
          isOpen ? "pb-4" : "h-0",
          textClass
        )}
        dangerouslySetInnerHTML={{
          __html: sanitizeOrPassthrough(localPurifier, item.content.value, { ssrTagClassMap: ssrTagClasses })
        }}
      />
    </div>
  );
};